package data.objects.views

import apiclient.FormationClient
import apiclient.mapsearch.MarkerCluster
import apiclient.mapsearch.getMarkers
import com.jillesvangurp.geo.tiles.Tile
import data.ObjectAndUserHandler
import data.objects.ActiveObjectStore
import dev.fritz2.core.RenderContext
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.invoke
import dev.fritz2.core.storeOf
import koin.koinCtx
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.cancel
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import localization.TL
import localization.translate
import mainmenu.RouterStore
import map.Cards
import map.MapStateStore
import maplibreGL.ClusterProperties
import maplibreGL.FormationMap
import mapsearch.ClusterService
import mapsearch.SearchSpecStore
import mapsearch.globalsearch.toMapMarker
import org.w3c.dom.HTMLElement
import search.simpleListEntryMapMarker
import svgmarker.defaultMarkerSizeSearch
import svgmarker.makeClusterMarkerSvg
import theme.FormationColors
import twcomponents.doOnceWhenElementInDOM
import twcomponents.twColOfNoGap
import twcomponents.twPageHeaderClose
import twcomponents.twRowOf
import twcomponents.twSubtitle
import twcomponents.twTitle
import utils.resolveColor
import webcomponents.baseLayout

class CurrentMarkerClusterStore : RootStore<MarkerCluster?>(
    initialData = null,
    job = Job(),
) {

    private val objectAndUserHandler: ObjectAndUserHandler by koinCtx.inject()
    private val formationMap: FormationMap by koinCtx.inject()
    private val routerStore: RouterStore by koinCtx.inject()
    private val mapStateStore: MapStateStore by koinCtx.inject()
    private val activeObjectStore: ActiveObjectStore by koinCtx.inject()

    val clusterContent = storeOf(setOf<apiclient.mapsearch.MapMarker>(), Job())

    private var fetchedMarkers: Set<apiclient.mapsearch.MapMarker> = emptySet()
    private var nextIndex: Int = 0
    private var total: Long = 0L
    private var fullyLoaded: Boolean = false

    val loading = storeOf(false, Job())

    private fun resetClusterFetchStates() {
        fetchedMarkers = emptySet()
        nextIndex = 0
        total = 0L
        fullyLoaded = false
    }

    val reset = handle {
        resetClusterFetchStates()
        null
    }

    val onObjectClick = handle<apiclient.mapsearch.MapMarker> { current, marker ->
        reset()
//        when (marker.objectType) {
//            ObjectType.UserMarker, ObjectType.CurrentUserMarker -> {
//                objectAndUserHandler.fetchAndShowUser(marker.id)
//            }
//
//            else -> objectAndUserHandler.fetchAndShowObject(marker.id)
//        }
        objectAndUserHandler.fetchAndShowObject(marker.id)
        current
    }

    val onClusterClick = handle<MarkerCluster> { current, cluster ->
        console.log("Cluster click", cluster.amount.toString(), cluster.markers?.size)
        routerStore.validateInternalRoute(Cards.ClusterPrev.route)
        coroutineScope {
            launch {
                routerStore.data.combine(mapStateStore.data) { r, _ -> r }.collect { route ->
                    if (route["card"] == Cards.ClusterPrev.name && formationMap.isNotMoving()) {
                        formationMap.panTo(cluster.position)
                        routerStore.addToMapClickListener()
//                        formationMap.removeActiveObjectOverride(activeObjectStore.current.id)
                        activeObjectStore.resetStore()
                        cancel()
                    }
                }
            }
        }
        if (cluster != current) {
            resetClusterFetchStates()
            val alreadyFetched = cluster.markers?.filterIsInstance<apiclient.mapsearch.MapMarker>()?.toSet() ?: emptySet()
            // Always add to global store for UI
            clusterContent.update(alreadyFetched)

            if (cluster.tilePath.isBlank()) {
                fullyLoaded = true
            } else {
                // For dynamic clusters, initialize or merge into existing state
                total = cluster.amount + alreadyFetched.size
                fetchedMarkers = alreadyFetched
                fetchNextPage(cluster)
            }
            cluster
        } else current
    }

    private suspend fun fetchNextPage(cluster: MarkerCluster, pageSize: Int = 25) {
        withKoin {
            val formationClient: FormationClient = get()
            val searchSpecStore: SearchSpecStore = get()
            val clusterService: ClusterService = get()

            val clusterPath = cluster.tilePath

            // Skip if static cluster (no paging)
            if (clusterPath.isBlank()) return

            if (fullyLoaded) return

            searchSpecStore.current?.let { searchSpec ->
                loading.update(true)
                val clusterTile = Tile.fromPath(clusterPath)
//                delay(1000) // some visible loading delay
                formationClient.getMarkers(
                    zoom = clusterTile.zoom,
                    tiles = listOf(clusterTile),
                    searchSpec = searchSpec,
                    size = pageSize,
                    from = nextIndex,
                ).fold(
                    { res ->
                        val markersInThisCluster = res.results.mapNotNull { it.toMapMarker() }
                            .filter { result ->
                                result.id !in clusterService.current.ids
                            }
                        val newMarkers = markersInThisCluster.toSet().subtract(fetchedMarkers)

                        fetchedMarkers += newMarkers
                        nextIndex += res.results.size

                        clusterContent.update(fetchedMarkers)

                        if (fetchedMarkers.size >= total || res.results.isEmpty()) {
                            fullyLoaded = true
                        }

                        loading.update(false)
                    },
                    { e ->
                        loading.update(false)
                        console.error("Failed to load more cluster content", e.message)
                    },
                )
            }
        }
    }

    val loadMore = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            current?.let { fetchNextPage(it) }
        }
    }

    init {
        // make sure cluster card is not visible if there is no cluster is in this store
        data handledBy { cluster ->
            if ((cluster == null || cluster.amount == 0L) && routerStore.current["card"] == Cards.ClusterPrev.name) {
//                console.log("** EMPTY CLUSTER CARD")
                routerStore.validateInternalRoute(routerStore.current.minus("card"))
            }
        }
    }
}

fun HTMLElement.onScrollToBottom(callback: () -> Unit) {
    this.addEventListener(
        "scroll",
        {
            if (this.scrollTop + this.clientHeight >= this.scrollHeight - 10) {
                callback()
            }
        },
    )
}

fun RenderContext.cardClusterPreview() {

    val currentMarkerClusterStore: CurrentMarkerClusterStore by koinCtx.inject()
    currentMarkerClusterStore.data.render { cluster ->
        if (cluster != null) {
            baseLayout(
                header = {
                    twPageHeaderClose(
                        additionalBackButtonHandler = currentMarkerClusterStore.reset,
                    ) {
                        twRowOf {
//                            currentMarkerClusterStore.clusterContent.data.render { fetchedContent ->
//                                p { +fetchedContent.size.toString() }
//                            }
                            div("flex grow-0 shrink-0 items-center justify-center", id = "icon-box") {
                                className("basis-[${defaultMarkerSizeSearch}px] w-[${defaultMarkerSizeSearch}px] h-[${defaultMarkerSizeSearch}px]")
                                svg {}.domNode.apply {
                                    outerHTML = makeClusterMarkerSvg(
                                        count = 0,
                                        amountLabel = null,
                                        props = ClusterProperties(
                                            objectTypes = emptyMap(),
                                            colors = cluster.colorBreakDown.map { (color, number) ->
                                                resolveColor(color) to number
                                            }.toMap().ifEmpty { mapOf(FormationColors.White.color to cluster.amount.toInt()) },
                                        ),
                                        withShadow = true,
                                        desaturated = false,
                                        cursorPointer = true,
                                    )
                                }
                            }
                            twColOfNoGap {
                                twTitle {
                                    translate(TL.CardClusterPrev.CLUSTER_PREVIEW)
                                }
                                twSubtitle {
                                    translate(TL.CardClusterPrev.OBJECTS_AMOUNT, mapOf("objects" to cluster.amount.toInt()))
                                }
                            }
                        }
                    }
                },
                content = {
                    twColOfNoGap("w-full h-full items-stretch justify-start overflow-y-auto") {

                        // Attach scroll listener
                        doOnceWhenElementInDOM(domNode = domNode) {
                            domNode.onScrollToBottom {
                                currentMarkerClusterStore.loadMore()
                            }
                        }

                        currentMarkerClusterStore.clusterContent.data.map { it.toList() }.renderEach(apiclient.mapsearch.MapMarker::id) { mapMarker ->
                            button(
                                "h-max hover:bg-gray-100",
                                id = "list-entry-button",
                            ) {
                                simpleListEntryMapMarker(mapMarker)
                                clicks.map { mapMarker } handledBy currentMarkerClusterStore.onObjectClick
                            }
                        }
                        currentMarkerClusterStore.loading.data.render { isLoading ->
                            if (isLoading) {
                                div("p-5 animation-pulse") {
                                    +"Loading more items..." // TODO translate
                                }
                            }
                        }
                    }
                },
            )
        }
    }
}
